<template>
  <seats-modal-shell
    :visible="true"
    title="Definir usuário da licença"
    :busy="isSubmitting"
    @close="$emit('close')">
    <div v-if="succeeded">
      <p class="seats-modal-success">{{ successMessage }}</p>
      <div class="seats-modal-footer">
        <button type="button" class="seats-modal-button seats-modal-button--primary" @click="$emit('close')">Entendido</button>
      </div>
    </div>

    <div v-else>
      <p class="seats-modal-text">
        A licença {{ seat.plan }} já está contratada e paga. Quem você escolher passa a usá-la agora, sem cobrança nova.
      </p>

      <div
        v-if="benefits.length > 0"
        class="seats-modal-summary assign-seat-benefits"
        :style="{ backgroundColor: planStyle.backgroundColor, border: '1px solid ' + planStyle.borderColor }">
        <span class="assign-seat-benefits-title">O que vem com esta licença</span>
        <ul class="assign-seat-benefits-list">
          <li v-for="benefit in benefits" :key="benefit">{{ benefit }}</li>
        </ul>
      </div>

      <label class="seats-modal-label" for="assign-seat-user">Quem vai usar</label>
      <p v-if="candidates.length === 0" class="seats-modal-text">
        Não há usuários desativados ou aguardando pagamento. Convide alguém para ocupar esta licença.
      </p>
      <select
        v-else
        id="assign-seat-user"
        v-model="selectedEmail"
        class="seats-modal-select"
        :disabled="isSubmitting">
        <option value="" disabled>Selecione o usuário</option>
        <option v-for="candidate in candidates" :key="candidate.email" :value="candidate.email">
          {{ candidate.name }} ({{ candidate.email }}){{ candidate.isAwaitingPayment ? ' · aguardando pagamento' : '' }}
        </option>
      </select>
      <p v-if="selectedCandidate && selectedCandidate.isAwaitingPayment" class="seats-modal-warning">
        A cobrança pendente dele será cancelada e ele passa a usar esta licença, sem cobrança nova.
      </p>
      <p v-if="seatIsLeaving" class="seats-modal-warning">
        Esta licença expira em {{ seatRemovalDay }}. O usuário poderá usá-la até {{ seatLastUsableDay }} e será desativado quando ela expirar.
      </p>

      <p v-if="error" class="seats-modal-error">{{ error }}</p>

      <div class="seats-modal-footer">
        <button type="button" class="seats-modal-button seats-modal-button--secondary" :disabled="isSubmitting" @click="$emit('close')">Cancelar</button>
        <button
          type="button"
          class="seats-modal-button seats-modal-button--primary"
          :disabled="submitBlocked"
          @click="submit">
          {{ isSubmitting ? 'Salvando...' : 'Confirmar' }}
        </button>
      </div>
    </div>
  </seats-modal-shell>
</template>

<script>
import { occupySeat } from '@/services/subscription'
import { friendlyPlainText } from '@/utils/error-message'
import { isLeavingSeat, formatSeatShortDay, formatSeatShortLastUsableDay } from '@/utils/seats'
import { getPlanBadgeStyle } from '@/utils/plan-badge'
import { planBenefitLabels } from '../../Subscription/pricing-comparison'
import SeatsModalShell from './SeatsModalShell.vue'

export default {
  name: 'AssignSeatModal',
  components: {
    SeatsModalShell
  },
  props: {
    seat: {
      type: Object,
      required: true
    },
    candidates: {
      type: Array,
      required: true
    },
    pricingList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      selectedEmail: '',
      isSubmitting: false,
      succeeded: false,
      successMessage: '',
      error: ''
    }
  },
  computed: {
    selectedCandidate () {
      return this.candidates.find(candidate => candidate.email === this.selectedEmail) || null
    },
    seatIsLeaving () {
      return isLeavingSeat(this.seat)
    },
    seatRemovalDay () {
      return this.seatIsLeaving ? formatSeatShortDay(this.seat.removalActivateAt) : ''
    },
    seatLastUsableDay () {
      return this.seatIsLeaving ? formatSeatShortLastUsableDay(this.seat.removalActivateAt) : ''
    },
    benefits () {
      const plan = this.pricingList.find(candidate => candidate.code === this.seat.planCode)
      return plan ? planBenefitLabels(plan) : []
    },
    planStyle () {
      return getPlanBadgeStyle(this.seat.planCode)
    },
    submitBlocked () {
      return this.isSubmitting || !this.selectedEmail
    }
  },
  methods: {
    async submit () {
      if (this.submitBlocked) return
      this.isSubmitting = true
      this.error = ''
      // Montada antes da chamada: o 'assigned' recarrega a lista e o usuário, agora
      // ativo, deixa de estar entre os candidatos.
      const candidate = this.selectedCandidate
      const activationVerb = candidate.isAwaitingPayment ? 'ativado' : 'reativado'
      const expiryNotice = this.seatIsLeaving ? ` A licença expira em ${this.seatRemovalDay}.` : ''
      try {
        await occupySeat(this.selectedEmail, this.seat.seatId)
        this.successMessage = `O usuário ${candidate.name} foi ${activationVerb} com a licença ${this.seat.plan} que estava livre e não gerou cobranças adicionais.${expiryNotice}`
        this.succeeded = true
        this.$emit('assigned')
      } catch (err) {
        this.error = friendlyPlainText(err, 'Não foi possível definir o usuário da licença. Tente novamente.')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.assign-seat-benefits {
  margin-top: 0;
  margin-bottom: 1.25rem;
}
.assign-seat-benefits-title {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}
.assign-seat-benefits-list {
  margin: 0;
  padding-left: 1.125rem;
  font-size: 0.8125rem;
  color: #374151;
}
</style>



// WEBPACK FOOTER //
// src/components/Users/seats/AssignSeatModal.vue